// How shortcuts are written, matched, recorded and shown.
//
// A shortcut is written like "Mod+Shift+K": modifiers, then one key. Mod is
// Ctrl on Windows and Linux and ⌘ on the Mac; Ctrl and Meta name those keys
// themselves. The key is a letter, a digit, a named key (Enter, PageDown,
// F8…) or the unshifted character of a US-layout punctuation key ([ ] , . /
// \ ; ' ` - =).
//
// Matching goes by physical key (KeyboardEvent.code), never by the character
// typed: Shift, Option and AltGr all change the character (Ctrl+Shift+.
// arrives as ">"), but not the key. See docs/keyboard-shortcut-proposal.md.

import { IS_MAC } from '../utils/appVariant'

const MODIFIERS = ['Mod', 'Ctrl', 'Meta', 'Alt', 'Shift'] as const
type Modifier = (typeof MODIFIERS)[number]

/** Punctuation keys by their unshifted US character. */
const PUNCTUATION_CODES: Record<string, string> = {
  '[': 'BracketLeft', ']': 'BracketRight', ',': 'Comma', '.': 'Period', '/': 'Slash',
  '\\': 'Backslash', ';': 'Semicolon', "'": 'Quote', '`': 'Backquote', '-': 'Minus', '=': 'Equal',
}
const PUNCTUATION_KEYS = Object.fromEntries(Object.entries(PUNCTUATION_CODES).map(([key, code]) => [code, key]))

/** Named keys whose code is their name. */
const NAMED_KEYS = new Set([
  'Enter', 'Backspace', 'Delete', 'Tab', 'Escape', 'Space', 'Home', 'End', 'PageUp', 'PageDown',
  'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Insert', 'ContextMenu',
  ...Array.from({ length: 12 }, (_, i) => `F${i + 1}`),
])

export interface ParsedShortcut {
  ctrl: boolean
  meta: boolean
  alt: boolean
  shift: boolean
  /** The physical keys it matches (a digit also matches its numpad key). */
  codes: string[]
  key: string
}

/** Splits "Mod+Shift+K" into its parts; null for a malformed shortcut. */
export function parseShortcut(shortcut: string, mac = IS_MAC): ParsedShortcut | null {
  if (!shortcut) return null
  // "+" separates parts, so a shortcut ending in "+" can't be written; "=" is the key.
  const parts = shortcut.split('+')
  const key = parts.pop()
  if (!key || parts.some((part) => !MODIFIERS.includes(part as Modifier))) return null
  const wants = new Set(parts as Modifier[])
  const codes = codesFor(key)
  if (!codes) return null
  return {
    ctrl: wants.has('Ctrl') || (wants.has('Mod') && !mac),
    meta: wants.has('Meta') || (wants.has('Mod') && mac),
    alt: wants.has('Alt'),
    shift: wants.has('Shift'),
    codes,
    key,
  }
}

function codesFor(key: string): string[] | null {
  if (/^[A-Z]$/i.test(key)) return [`Key${key.toUpperCase()}`]
  if (/^[0-9]$/.test(key)) return [`Digit${key}`, `Numpad${key}`]
  if (PUNCTUATION_CODES[key]) {
    const code = PUNCTUATION_CODES[key]
    // The numpad's own - and = count as the same key (zoom out, zoom in).
    if (key === '-') return [code, 'NumpadSubtract']
    if (key === '=') return [code, 'NumpadAdd']
    return [code]
  }
  if (NAMED_KEYS.has(key)) return [key]
  return null
}

/** Whether a keydown matches a shortcut. */
export function matchesShortcut(e: KeyboardEvent, shortcut: string, mac = IS_MAC): boolean {
  const parsed = parseShortcut(shortcut, mac)
  if (!parsed) return false
  if (e.ctrlKey !== parsed.ctrl || e.metaKey !== parsed.meta || e.altKey !== parsed.alt || e.shiftKey !== parsed.shift) return false
  if (parsed.codes.includes(e.code)) return true
  // Some keyboards and test tools report no physical key; fall back to the
  // character, which only works for keys Shift doesn't change.
  return !e.code && !parsed.shift && e.key.toLowerCase() === parsed.key.toLowerCase()
}

/** The key part of a shortcut from a keydown's physical key, or null for keys a shortcut can't use. */
function keyFromCode(code: string): string | null {
  if (/^Key[A-Z]$/.test(code)) return code.slice(3)
  if (/^(Digit|Numpad)[0-9]$/.test(code)) return code.slice(-1)
  if (PUNCTUATION_KEYS[code]) return PUNCTUATION_KEYS[code]
  if (code === 'NumpadSubtract') return '-'
  if (code === 'NumpadAdd') return '='
  if (NAMED_KEYS.has(code)) return code
  return null
}

/**
 * A shortcut from a keydown, for recording one in Settings. Null for a lone
 * modifier, or a key without Ctrl, ⌘ or Alt (unless it's a function key, or
 * `bare` allows plain keys, for keys that only work inside one tool).
 */
export function shortcutFromEvent(e: KeyboardEvent, mac = IS_MAC, bare = false): string | null {
  if (['Control', 'Meta', 'Alt', 'Shift', 'AltGraph'].includes(e.key)) return null
  const key = keyFromCode(e.code) ?? (e.key.length === 1 ? e.key.toUpperCase() : null)
  if (!key) return null
  const functionKey = /^F[0-9]+$/.test(key)
  if (!bare && !e.ctrlKey && !e.metaKey && !e.altKey && !functionKey) return null
  const parts: string[] = []
  if (mac ? e.metaKey : e.ctrlKey) parts.push('Mod')
  if (mac && e.ctrlKey) parts.push('Ctrl')
  if (!mac && e.metaKey) parts.push('Meta')
  if (e.altKey) parts.push('Alt')
  if (e.shiftKey) parts.push('Shift')
  return [...parts, key].join('+')
}

const MAC_GLYPHS: Record<string, string> = { Ctrl: '⌃', Alt: '⌥', Shift: '⇧', Mod: '⌘', Meta: '⌘' }
// The order the Mac shows modifiers in: ⌃ ⌥ ⇧ ⌘.
const MAC_ORDER = ['Ctrl', 'Alt', 'Shift', 'Mod', 'Meta']
const KEY_NAMES: Record<string, string> = {
  ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→', PageUp: 'PgUp', PageDown: 'PgDn', Escape: 'Esc', Delete: 'Del',
}
const MAC_KEY_NAMES: Record<string, string> = { ...KEY_NAMES, Enter: '↩', Backspace: '⌫', Delete: '⌦', Escape: '⎋', Tab: '⇥' }

/** A shortcut as the writer reads it: "Ctrl+Shift+K", or "⇧⌘K" on the Mac. */
export function formatShortcut(shortcut: string, mac = IS_MAC): string {
  if (!shortcut) return ''
  const parts = shortcut.split('+')
  const key = parts.pop() ?? ''
  if (mac) {
    const mods = MAC_ORDER.filter((mod) => parts.includes(mod)).map((mod) => MAC_GLYPHS[mod]).join('')
    return mods + (MAC_KEY_NAMES[key] ?? key)
  }
  const mods = parts.map((part) => (part === 'Mod' ? 'Ctrl' : part === 'Meta' ? 'Win' : part))
  return [...mods, KEY_NAMES[key] ?? key].join('+')
}

// ── Keys the browsers keep ──────────────────────────────────────────────────
// From Chromium's IsReservedCommandOrKey and Firefox's reserved="true" keys,
// plus Safari's reported list (see "Browser check" in the proposal). A page
// can't take these in the web build, so they're only ever desktop extras.

const RESERVED_ELSEWHERE = [
  'Mod+N', 'Mod+Shift+N', 'Mod+T', 'Mod+Shift+T', 'Mod+W', 'Mod+Shift+W', 'Mod+Shift+P', 'Mod+Q', 'Mod+Shift+Q',
  'Ctrl+Tab', 'Ctrl+Shift+Tab', 'Ctrl+PageUp', 'Ctrl+PageDown', 'Ctrl+F4',
]
const RESERVED_SAFARI = ['Mod+R', 'Mod+L', 'Ctrl+Home']

/** Whether a browser keeps this shortcut for itself, so it can't work in the web build. */
export function isBrowserReserved(shortcut: string, mac = IS_MAC): boolean {
  const target = parseShortcut(shortcut, mac)
  if (!target) return false
  const list = mac ? [...RESERVED_ELSEWHERE, ...RESERVED_SAFARI] : RESERVED_ELSEWHERE
  return list.some((reserved) => {
    const parsed = parseShortcut(reserved, mac)
    return !!parsed && parsed.ctrl === target.ctrl && parsed.meta === target.meta && parsed.alt === target.alt
      && parsed.shift === target.shift && parsed.codes[0] === target.codes[0]
  })
}

/** Whether two shortcuts are the same keys (on this platform). */
export function sameShortcut(a: string, b: string, mac = IS_MAC): boolean {
  const x = parseShortcut(a, mac)
  const y = parseShortcut(b, mac)
  return !!x && !!y && x.ctrl === y.ctrl && x.meta === y.meta && x.alt === y.alt && x.shift === y.shift && x.codes[0] === y.codes[0]
}

/** Keys the keyboard navigation rules keep (docs/keyboard-navigation.md); no shortcut may take them. */
const NAVIGATION_KEYS = ['F6', 'Shift+F6', 'Shift+F10', 'ContextMenu', 'Ctrl+Tab', 'Ctrl+Shift+Tab', 'Escape', 'Tab', 'Shift+Tab']

export function isNavigationKey(shortcut: string, mac = IS_MAC): boolean {
  return NAVIGATION_KEYS.some((key) => sameShortcut(key, shortcut, mac))
}

/** Writes a shortcut parsed from any order of modifiers in the canonical order: Mod, Ctrl, Meta, Alt, Shift. */
export function normalizeShortcut(shortcut: string): string | null {
  const parts = shortcut.split('+')
  const key = parts.pop()
  if (!key || !parseShortcut(shortcut)) return null
  const order = ['Mod', 'Ctrl', 'Meta', 'Alt', 'Shift']
  return [...order.filter((mod) => parts.includes(mod)), key].join('+')
}
